/**
 * ForvaltningsberattelsePage - Guide about management report
 *
 * SEO Target: /arsredovisning/guider/forvaltningsberattelse
 * Primary keywords: förvaltningsberättelse, flerårsöversikt, resultatdisposition
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export function ForvaltningsberattelsePage() {
  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider", href: "/arsredovisning/guider" },
    { label: "Förvaltningsberättelse" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
    { name: "Förvaltningsberättelse", url: "/arsredovisning/guider/forvaltningsberattelse" },
  ]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Förvaltningsberättelsen - Vad ska den innehålla?",
    description:
      "Komplett guide om förvaltningsberättelsen i årsredovisningen. Verksamhetsbeskrivning, flerårsöversikt, resultatdisposition och mer.",
    author: {
      "@type": "Organization",
      name: "Kvanten",
    },
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      url: SEO_CONFIG.siteUrl,
    },
    datePublished: "2025-01-20",
    dateModified: "2025-01-30",
  };

  return (
    <>
      <PageMeta
        title="Förvaltningsberättelsen | Vad Ska Den Innehålla? | Kvanten"
        description="Guide om förvaltningsberättelsen i årsredovisningen. Lär dig skriva verksamhetsbeskrivning, flerårsöversikt och resultatdisposition korrekt."
        keywords="förvaltningsberättelse, flerårsöversikt, resultatdisposition, verksamhetsbeskrivning, årsredovisning"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider/forvaltningsberattelse`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <span>Guide</span>
            <span>•</span>
            <span>8 min läsning</span>
            <span>•</span>
            <span>Uppdaterad jan 2025</span>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Förvaltningsberättelsen: Vad ska den innehålla?
          </h1>

          <p className="text-xl text-gray-600">
            Förvaltningsberättelsen är den beskrivande delen av årsredovisningen. Den ger en
            översiktlig bild av företagets verksamhet, utveckling och ställning. Här går vi igenom
            vad som krävs enligt K2.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Innehåll</h2>
            <nav>
              <ol className="space-y-2 text-blue-600">
                <li>
                  <a href="#oversikt" className="hover:underline">
                    1. Översikt
                  </a>
                </li>
                <li>
                  <a href="#verksamhet" className="hover:underline">
                    2. Allmänt om verksamheten
                  </a>
                </li>
                <li>
                  <a href="#flerarsversikt" className="hover:underline">
                    3. Flerårsöversikt
                  </a>
                </li>
                <li>
                  <a href="#eget-kapital" className="hover:underline">
                    4. Förändring av eget kapital
                  </a>
                </li>
                <li>
                  <a href="#resultatdisposition" className="hover:underline">
                    5. Resultatdisposition
                  </a>
                </li>
                <li>
                  <a href="#exempel" className="hover:underline">
                    6. Exempel på formuleringar
                  </a>
                </li>
              </ol>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="oversikt" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Översikt</h2>
            <p className="text-gray-600 mb-4">
              Förvaltningsberättelsen är den första delen av årsredovisningen och ska enligt ÅRL 6
              kap innehålla en rättvisande översikt av utvecklingen av företagets verksamhet,
              ställning och resultat.
            </p>
            <p className="text-gray-600 mb-4">
              För mindre företag enligt K2 ska förvaltningsberättelsen innehålla följande delar:
            </p>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-3">Obligatoriskt innehåll (K2)</h3>
              <ul className="space-y-2">
                {[
                  "Allmänt om verksamheten (art, inriktning, säte)",
                  "Väsentliga händelser under räkenskapsåret",
                  "Flerårsöversikt med nyckeltal",
                  "Förändring av eget kapital",
                  "Förslag till resultatdisposition",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="text-blue-800">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section id="verksamhet" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Allmänt om verksamheten</h2>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Verksamhetens art och inriktning</h3>
                <p className="text-gray-600 mb-3">
                  En kort beskrivning av vad företaget gör. Ska ge läsaren en förståelse för
                  verksamheten.
                </p>
                <div className="bg-gray-50 rounded p-4 text-sm text-gray-600">
                  <strong>Exempel:</strong> "Bolaget bedriver konsultverksamhet inom IT-området med
                  fokus på systemutveckling och teknisk projektledning."
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Säte</h3>
                <p className="text-gray-600 mb-3">
                  Ange i vilken kommun styrelsen har sitt säte.
                </p>
                <div className="bg-gray-50 rounded p-4 text-sm text-gray-600">
                  <strong>Exempel:</strong> "Styrelsen har sitt säte i Stockholm."
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Väsentliga händelser under räkenskapsåret
                </h3>
                <p className="text-gray-600 mb-3">
                  Beskriv sådant som påverkat företagets verksamhet, resultat eller ställning på ett
                  väsentligt sätt.
                </p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Viktiga avtal som ingåtts eller sagts upp</li>
                  <li>• Nya produkter/tjänster</li>
                  <li>• Väsentliga investeringar</li>
                  <li>• Förändringar i organisation</li>
                  <li>• Om inget väsentligt hänt - skriv det</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Väsentliga händelser efter räkenskapsårets slut
                </h3>
                <p className="text-gray-600 mb-3">
                  Händelser som inträffat mellan balansdagen och dagen då årsredovisningen
                  undertecknas ska tas upp om de är väsentliga.
                </p>
                <div className="bg-amber-50 rounded p-4 text-sm text-amber-800">
                  <strong>OBS:</strong> Om inga väsentliga händelser inträffat behöver du inte
                  skriva något alls om detta. Det är inte ett krav att ange "Inga väsentliga
                  händelser har inträffat" även om det är vanligt.
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="flerarsversikt" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Flerårsöversikt</h2>
            <p className="text-gray-600 mb-4">
              Flerårsöversikten ska enligt K2 (kapitel 5.5) omfatta de{" "}
              <strong>fyra senaste räkenskapsåren</strong> och innehålla minst följande nyckeltal:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left py-3 px-4 border font-semibold">Nyckeltal</th>
                    <th className="text-right py-3 px-4 border font-semibold">2024</th>
                    <th className="text-right py-3 px-4 border font-semibold">2023</th>
                    <th className="text-right py-3 px-4 border font-semibold">2022</th>
                    <th className="text-right py-3 px-4 border font-semibold">2021</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-3 px-4 border">Nettoomsättning (tkr)</td>
                    <td className="py-3 px-4 border text-right">1 500</td>
                    <td className="py-3 px-4 border text-right">1 200</td>
                    <td className="py-3 px-4 border text-right">950</td>
                    <td className="py-3 px-4 border text-right">800</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border">Resultat efter finansiella poster (tkr)</td>
                    <td className="py-3 px-4 border text-right">150</td>
                    <td className="py-3 px-4 border text-right">100</td>
                    <td className="py-3 px-4 border text-right">75</td>
                    <td className="py-3 px-4 border text-right">50</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border">Soliditet (%)</td>
                    <td className="py-3 px-4 border text-right">45</td>
                    <td className="py-3 px-4 border text-right">40</td>
                    <td className="py-3 px-4 border text-right">35</td>
                    <td className="py-3 px-4 border text-right">30</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Soliditet</h4>
                <p className="text-sm text-gray-600">
                  Justerat eget kapital / Totala tillgångar × 100
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Justerat EK = EK + 79,4% av obeskattade reserver
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Första året?</h4>
                <p className="text-sm text-gray-600">
                  Om bolaget är nystartat kan flerårsöversikten innehålla färre år. Det är OK.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="eget-kapital" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Förändring av eget kapital</h2>
            <p className="text-gray-600 mb-4">
              Enligt K2 (kapitel 5.7) ska förvaltningsberättelsen innehålla en specifikation av
              förändringen i eget kapital. Detta kan presenteras antingen i förvaltningsberättelsen
              eller som en egen not.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left py-3 px-4 border font-semibold"></th>
                    <th className="text-right py-3 px-4 border font-semibold">Aktiekapital</th>
                    <th className="text-right py-3 px-4 border font-semibold">Balanserat</th>
                    <th className="text-right py-3 px-4 border font-semibold">Årets resultat</th>
                    <th className="text-right py-3 px-4 border font-semibold">Summa</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Ingående balans</td>
                    <td className="py-3 px-4 border text-right">50 000</td>
                    <td className="py-3 px-4 border text-right">75 000</td>
                    <td className="py-3 px-4 border text-right">25 000</td>
                    <td className="py-3 px-4 border text-right">150 000</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border">Disposition enl. årsstämma:</td>
                    <td className="py-3 px-4 border text-right"></td>
                    <td className="py-3 px-4 border text-right"></td>
                    <td className="py-3 px-4 border text-right"></td>
                    <td className="py-3 px-4 border text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border pl-6">- Utdelning</td>
                    <td className="py-3 px-4 border text-right">-</td>
                    <td className="py-3 px-4 border text-right">-10 000</td>
                    <td className="py-3 px-4 border text-right">-</td>
                    <td className="py-3 px-4 border text-right">-10 000</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border pl-6">- Balanseras i ny räkning</td>
                    <td className="py-3 px-4 border text-right">-</td>
                    <td className="py-3 px-4 border text-right">25 000</td>
                    <td className="py-3 px-4 border text-right">-25 000</td>
                    <td className="py-3 px-4 border text-right">0</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Årets resultat</td>
                    <td className="py-3 px-4 border text-right">-</td>
                    <td className="py-3 px-4 border text-right">-</td>
                    <td className="py-3 px-4 border text-right">35 000</td>
                    <td className="py-3 px-4 border text-right">35 000</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 border font-semibold">Utgående balans</td>
                    <td className="py-3 px-4 border text-right font-semibold">50 000</td>
                    <td className="py-3 px-4 border text-right font-semibold">90 000</td>
                    <td className="py-3 px-4 border text-right font-semibold">35 000</td>
                    <td className="py-3 px-4 border text-right font-semibold">175 000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5 */}
          <section id="resultatdisposition" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Resultatdisposition</h2>
            <p className="text-gray-600 mb-4">
              Förvaltningsberättelsen ska innehålla styrelsens förslag till hur årets resultat ska
              disponeras. Detta fastställs sedan av årsstämman.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                Styrelsens förslag till vinstdisposition
              </h3>
              <p className="text-gray-600 mb-4">Till bolagsstämmans förfogande står:</p>
              <div className="space-y-1 text-sm text-gray-700 mb-4 font-mono">
                <div className="flex justify-between">
                  <span>Balanserat resultat</span>
                  <span>90 000 kr</span>
                </div>
                <div className="flex justify-between">
                  <span>Årets resultat</span>
                  <span className="border-b border-gray-400">35 000 kr</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Summa</span>
                  <span>125 000 kr</span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">Styrelsen föreslår:</p>
              <div className="space-y-1 text-sm text-gray-700 font-mono">
                <div className="flex justify-between">
                  <span>Till aktieägarna utdelas</span>
                  <span>20 000 kr</span>
                </div>
                <div className="flex justify-between">
                  <span>I ny räkning överföres</span>
                  <span className="border-b border-gray-400">105 000 kr</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Summa</span>
                  <span>125 000 kr</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h4 className="font-semibold text-amber-900 mb-2">Viktigt om utdelning</h4>
              <p className="text-amber-800">
                Föreslagen utdelning kan aldrig överstiga fritt eget kapital (balanserat resultat +
                årets resultat + eventuell fri överkursfond). Kvanten validerar automatiskt att
                utdelningen ligger inom tillåtet intervall.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section id="exempel" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Exempel på formuleringar</h2>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Verksamhetsbeskrivning</h3>
                <div className="bg-gray-50 rounded p-4 text-sm text-gray-600 italic">
                  "Bolaget bedriver konsultverksamhet inom ekonomi och redovisning. Verksamheten
                  bedrivs huvudsakligen från företagets lokaler i Stockholm. Bolaget har under året
                  haft [X] kunder."
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Väsentliga händelser</h3>
                <div className="bg-gray-50 rounded p-4 text-sm text-gray-600 italic">
                  "Under året har bolaget tecknat ett nytt samarbetsavtal med [Företag AB] som
                  förväntas bidra positivt till omsättningen kommande år. Bolaget har även
                  investerat i nya kontorslokaler."
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Om inga väsentliga händelser inträffat
                </h3>
                <div className="bg-gray-50 rounded p-4 text-sm text-gray-600 italic">
                  "Inga händelser av väsentlig betydelse för bolagets ställning har inträffat under
                  räkenskapsåret."
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Framtida utveckling (frivilligt)</h3>
                <div className="bg-gray-50 rounded p-4 text-sm text-gray-600 italic">
                  "Bolaget förväntar sig en fortsatt stabil utveckling under kommande räkenskapsår.
                  Fokus kommer ligga på att utveckla befintliga kundrelationer och utöka
                  tjänsteutbudet."
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Kvanten genererar förvaltningsberättelsen
            </h2>
            <p className="text-orange-100 mb-6">
              Flerårsöversikt, eget kapital-förändring och resultatdisposition skapas automatiskt
              från din SIE-fil. Du fyller bara i verksamhetsbeskrivningen.
            </p>
            <Link
              to="/registrera"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-orange-600 bg-white rounded-lg hover:bg-orange-50 transition-colors"
            >
              Kom igång gratis
            </Link>
          </section>

          {/* Related articles */}
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Relaterade guider</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Noter i årsredovisningen",
                  href: "/arsredovisning/guider/noter",
                  description: "Guide om vilka noter som krävs",
                },
                {
                  title: "Första årsredovisningen",
                  href: "/arsredovisning/guider/forsta-arsredovisningen",
                  description: "Komplett guide för nystartade företag",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  to={article.href}
                  className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600">{article.description}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
